import type { QueryKey, UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import type { ProposalContractReadinessDto } from "../dto/proposal-contract.dto"

/** Poll while DocuSign outcome is still being matched for an open Contract drawer. */
export const CONTRACT_RECONCILE_POLL_MS = 5_000

const latestHistoryStatus = (
  readiness: ProposalContractReadinessDto | undefined,
): string | null => {
  if (!readiness?.history.length) {
    return null
  }
  const primary = readiness.history.filter(
    (entry) => entry.contractType === `primary`,
  )
  const latest = (primary.length > 0 ? primary : readiness.history).at(-1)
  return latest?.status ?? null
}

export const shouldPollContractReadiness = (
  readiness: ProposalContractReadinessDto | undefined,
): boolean => {
  // Envelope-id collisions and multi-match ambiguity need support — stop polling.
  if (
    readiness?.blockers.some((entry) => entry.code === `SEND_UNKNOWN_AMBIGUOUS`)
  ) {
    return false
  }
  if (readiness?.lifecycleRefreshPending) {
    return true
  }
  const status = latestHistoryStatus(readiness)
  return status === `sending` || status === `send_unknown`
}

/**
 * Builds a kind's contract readiness hook. The read runs a live Contracting
 * preflight, so it is fetched only while the Contract drawer is open and never
 * on window focus. While the latest attempt is still reconciling, poll so an
 * open drawer can discover a later unique DocuSign match.
 */
export const createContractReadinessHook = <
  R extends ProposalContractReadinessDto,
>({
  queryKey,
  fetchReadiness,
  missingIdMessage,
}: {
  queryKey: (proposalId: string) => QueryKey
  fetchReadiness: (proposalId: string) => Promise<R>
  missingIdMessage: string
}): ((proposalId: string | undefined) => UseQueryResult<R>) => {
  const useContractReadiness = (
    proposalId: string | undefined,
  ): UseQueryResult<R> =>
    useQuery({
      queryKey: queryKey(proposalId ?? ``),
      queryFn: () => {
        if (!proposalId) {
          throw new Error(missingIdMessage)
        }

        return fetchReadiness(proposalId)
      },
      enabled: Boolean(proposalId),
      retry: 1,
      // Each read costs two Contracting calls. Price- and savings-dependent
      // commands (including a superseded race) invalidate it.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      refetchInterval: (data) =>
        shouldPollContractReadiness(data) ? CONTRACT_RECONCILE_POLL_MS : false,
    })
  return useContractReadiness
}
